import { HasIndex } from '../modules/base'
const faker = require('faker')

export interface CanImportSeed {
  createdDate?: Date,
  updatedDate?: Date,
  importedId?: number
}

export abstract class Seeder<TAllTestData extends HasIndex, TAllLiveData extends HasIndex, TModel> {
  public nextId: number = 1
  faker: {
    name: {
      firstName: Function
      lastName: Function
    }
    internet: {
      email: Function
    }
    lorem: { paragraph: () => string }
  }

  constructor () {
    // getNextId will only be used in test data so miss the id numbers from live (which starts at 1).
    const liveData = this.getLiveSeed() || []
    this.nextId = liveData.length + 1
    this.faker = {
      name: {
        firstName: () => '',
        lastName: () => ''
      },
      internet: {
        email: () => ''
      },
      lorem: {
        paragraph: () => ''
      }
    }
  }

  abstract maxModelCount: number
  abstract type: string

  abstract getLiveDataObject (): TAllLiveData | undefined
  abstract getTestDataObject (): TAllTestData | undefined

  /**
   * Can be overridden on a derived type to provide a faked model for test data
   * @param id
   */
  public getFakeSeed (id: number): TModel | undefined {
    return void 0
  }

  /**
   * Flat array of getLiveDataObject without the keys
   */
  public getLiveSeed (): TModel[] {
    return this.flattenSeedDataObject(this.getLiveDataObject())
  }

  /**
   * Flat array of getLiveDataObject without the keys
   */
  getTestSeed (): TModel[] {
    this.faker = faker
    return this.flattenSeedDataObject(this.getTestDataObject())
  }

  /**
   * NOTE: For testing data ONLY! Uses live seed as an offset
   */
  getNextId () {
    return this.nextId++
  }

  generateRandomData (): TModel[] {
    const random: TModel[] = []
    let nextId = this.getNextId()
    while (nextId <= this.maxModelCount) {
      const model = this.getFakeSeed(nextId)
      if (model !== void 0) {
        random.push(model)
        nextId = this.getNextId()
      }
    }
    return random
  }

  getSeedWithId<T extends object, TT extends { id: number }> (seedData: T, id: number): TT | undefined {
    for (const key in seedData) {
      if (seedData.hasOwnProperty(key)) {
        const model = seedData[key] as unknown as TT
        if (model.id === id) {
          return model
        }
      }
    }
    return void 0
  }

  /**
   * Take an object like { someKey: SomeSeedModel, someOtherKey: SomeSeedModel } and return SomeSeedModel[]
   * @param data
   */
  flattenSeedDataObject (data: HasIndex | undefined): TModel[] {
    if (!data) return []

    let result: TModel[] = []
    for (const key in data) {
      const model = data[key]
      if (Array.isArray(model)) {
        result = result.concat(model)
      } else {
        result.push(model)
      }
    }
    return result
  }
}
